import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { PanelTop } from 'lucide-react';
import TotoNavigationGlyph from '../TotoNavigationGlyph';
import NavigationProfileAvatar from './NavigationProfileAvatar';
import { totoCollarCode, totoCollarNavigationColor } from '@/lib/totoCollarIdentity';
import './ContinuousVesselNavigation.css';
import './SculptedVessel.css';
import '@/styles/quiet-charcoal.css';
import './VesselHeader.css';
import './PrimaryVesselRail.css';
import { attachGlassRail } from './glassRail';
import './GlassVesselRail.css';

// Shared geometry keeps the contour and its controls aligned at every height.
export function edgeGeometry(height, count = 4, hasDestinations = true, compact = false) {
  const slot = compact ? Math.max(200, Math.min(220, height - 480)) : Math.max(260, Math.min(280, height - 480));
  const secondary = hasDestinations ? slot + 88 : 0;
  const primaryHeight = count * 52 + Math.max(0, count - 1) * 6;
  const end = height - 16 - secondary - (hasDestinations ? 12 : 0);
  const start = Math.max(138, end - primaryHeight - 44);
  return {slot, secondary, start, end: start + primaryHeight + 44};
}
export function vesselContour(width, height, geometry = edgeGeometry(height)) {
  const w = Math.max(0, width), h = Math.max(0, height), e = w - (w < 700 ? 56 : 76);
  const {start, end} = geometry;
  return `M 36 0 H ${w-36} Q ${w} 0 ${w} 36 V ${start} C ${w} ${start+22} ${e} ${start+22} ${e} ${start+44} V ${end-44} C ${e} ${end-22} ${w} ${end-22} ${w} ${end} V ${h-36} Q ${w} ${h} ${w-36} ${h} H 36 Q 0 ${h} 0 ${h-36} V 36 Q 0 0 36 0 Z`;
}
export function navigationInset(width, height, geometry = edgeGeometry(height)) {
  const w = Math.max(0, width), e = w - (w < 700 ? 56 : 76), {start, end} = geometry;
  return `M ${w} ${start} C ${w} ${start+22} ${e} ${start+22} ${e} ${start+44} V ${end-44} C ${e} ${end-22} ${w} ${end-22} ${w} ${end} Z`;
}
function Glyph({item}) { return item.Icon ? <item.Icon size={24} strokeWidth={1.55} aria-hidden="true"/> : item.glyph ? <TotoNavigationGlyph name={item.glyph} size={24}/> : <PanelTop size={24} aria-hidden="true"/>; }
export default function ContinuousVesselNavigation({host, primaryItems, destinations, tools, label, active, select, onToto, onProfile, user, navigationPhoto}) {
  const location = useLocation();
  const [bounds, setBounds] = useState(null);
  const railRef = useRef(null);
  const [capsule, setCapsule] = useState(null);
  useLayoutEffect(() => {
    if (!railRef.current) return undefined;
    return attachGlassRail(railRef.current, {buttonSelector: ":scope > .continuous-primary-destination:not(:disabled)"});
  }, [host, Boolean(bounds), primaryItems.map(item => item.id).join('|'), capsule?.top, capsule?.width, capsule?.height]);
  const [toolHost, setToolHost] = useState(null);
  const [nativeLabels, setNativeLabels] = useState([]);
  // Approved option 1 (2026-10-08): material follows the existing control
  // positions; it never participates in flex layout or changes the rail envelope.
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    let mounted = true;
    const measure = () => {
      if (!mounted) return;
      const buttons = [...rail.querySelectorAll('.continuous-primary-destination')];
      if (!buttons.length) { setCapsule(null); return; }
      const first = buttons[0].querySelector('.continuous-primary-socket').getBoundingClientRect();
      const last = buttons[buttons.length - 1].getBoundingClientRect();
      const frame = rail.getBoundingClientRect();
      const horizontal = getComputedStyle(rail).flexDirection === 'row';
      const sideWidth = parseFloat(getComputedStyle(rail).getPropertyValue("--side-rail-width")) || 52;
      const step = sideWidth === 56 ? 44 : 52;
      const inset = sideWidth === 56 ? 6 : 4;
      const compactHeight = buttons.length * step + inset * 2;
      const utilityEnd = rail.querySelector('.continuous-primary-profile').getBoundingClientRect().bottom-frame.top+rail.scrollTop;
      const next = horizontal
        ? {left:first.left-frame.left+rail.scrollLeft-6, top:first.top-frame.top+rail.scrollTop-6,
          width:last.right-first.left+8, height:64}
        : {left:(frame.width-sideWidth)/2, top:Math.max(utilityEnd+(sideWidth===56?24:12),frame.height-(sideWidth===56?0:7)-compactHeight),
          width:sideWidth, height:compactHeight};
      setCapsule(old => old && Object.keys(next).every(key=>Math.abs(old[key]-next[key])<.25) ? old : next);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(rail);
    rail.querySelectorAll('button').forEach(button=>observer?.observe(button));
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);
    return () => { mounted = false; observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [bounds, primaryItems, active]);
  useLayoutEffect(() => {
    if (!host) return undefined;
    const slot = document.createElement("div");
    slot.className = "continuous-page-tools-slot";
    const attach = () => {
      if (host.contains(slot)) return;
      const content = host.querySelector("[data-today-content]");
      if (content) { content.prepend(slot); return; }
      const designated = host.querySelector("[data-vessel-page-navigation]");
      if (designated) { designated.append(slot); return; }
      const views = host.querySelector(".today-schedule__views");
      if (views) { views.before(slot); return; }
      const header = host.querySelector(":scope > header, :scope > [data-vessel-content-slot] > header, :scope > .payroll-team-plane > header")
        || host.querySelector('h1')?.closest('[class*="__heading"],[class*="-heading"]');
      if (header) header.after(slot); else host.prepend(slot);
    };
    attach();
    // Some tools replace the host's inner page without replacing the Vessel.
    // Keep the portal attached when React removes that previous inner page.
    const observer = new MutationObserver(attach);
    observer.observe(host, {childList:true,subtree:true});
    setToolHost(slot);
    return () => {observer.disconnect();slot.remove();setToolHost(null);};
  },[host]);
  // Mark only the page title, including inner pages replaced inside a vessel.
  useLayoutEffect(() => {
    if (!host) return undefined;
    let title;
    let header;
    const mark = () => {
      const next = [...host.querySelectorAll('h1')].find(node => !node.closest('[role="dialog"],dialog,.journey-planning-print'));
      if (next === title) return;
      title?.removeAttribute('data-vessel-heading');
      header?.removeAttribute('data-vessel-header');
      title = next;
      header = title?.closest('header');
      title?.setAttribute('data-vessel-heading', 'true');
      if (header && host.contains(header)) header.setAttribute('data-vessel-header', 'true');
    };
    mark();
    const observer = new MutationObserver(mark);
    observer.observe(host, {childList:true,subtree:true});
    return () => {
      observer.disconnect();
      title?.removeAttribute('data-vessel-heading');
      header?.removeAttribute('data-vessel-header');
    };
  }, [host]);
  const selected = item => item.selected ?? Boolean(item.to && (location.pathname === item.to || location.pathname.startsWith(item.to + '/') || (item.id === 'payroll' && location.pathname.startsWith('/payroll/'))));
  // Existing page controls retain ownership; only missing destinations are added.
  useLayoutEffect(() => {
    if (!host) return undefined;
    const read = () => {
      const labels = [...host.querySelectorAll('button,a')]
        .filter(node => !node.closest('.continuous-page-tools-slot'))
        .map(node => (node.getAttribute('aria-label') || node.textContent).trim());
      setNativeLabels(old => JSON.stringify(old) === JSON.stringify(labels) ? old : labels);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(host, {childList:true,subtree:true,characterData:true});
    return () => observer.disconnect();
  }, [host]);
  useLayoutEffect(() => {
    if (!host) return undefined;
    const parent = host.parentElement;
    host.dataset.continuousVessel = 'true';
    host.style.setProperty('--continuous-secondary-height', '0px');
    parent.dataset.continuousVesselPage = 'true';
    const measure = () => {
      const rect = host.getBoundingClientRect();
      const next = {x:rect.left,y:rect.top,w:rect.width,h:rect.height};
      host.style.setProperty('--continuous-vessel-clip', 'inset(0 round 36px)');
      setBounds(old => old && Object.keys(next).every(key => old[key] === next[key]) ? old : next);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(host);
    window.addEventListener('resize',measure);
    window.addEventListener('scroll',measure,true);
    return () => {observer?.disconnect();window.removeEventListener('resize',measure);window.removeEventListener('scroll',measure,true);delete host.dataset.continuousVessel;delete parent.dataset.continuousVesselPage;host.style.removeProperty('--continuous-vessel-clip');host.style.removeProperty('--continuous-secondary-height');};
  },[host,primaryItems.length,destinations.length]);
  const pageButton = item => {
    const props = {'aria-label':item.label, 'aria-current':selected(item)?'page':undefined, className:'continuous-page-destination'};
    return item.to && !item.disabled
      ? <Link key={item.id||item.label} {...props} to={item.to} onClick={event=>{if(!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button===0){event.preventDefault();select(item);}}}>{item.label}</Link>
      : <button key={item.id||item.label} {...props} type="button" disabled={item.disabled} onClick={()=>select(item)}>{item.label}</button>;
  };
  const missing = destinations.filter(item => !nativeLabels.includes(item.label));
  const contentTools = toolHost ? createPortal(<>
    {missing.length>0 && <nav className="continuous-page-navigation" aria-label={label}>
      <div className="continuous-page-navigation__sections">{missing.filter(item=>!item.to).map(pageButton)}</div>
      <div className="continuous-page-navigation__links">{missing.filter(item=>item.to).map(pageButton)}</div>
    </nav>}
    {tools.length>0 && <div className="continuous-page-tools" role="group" aria-label="Page tools"><span className="continuous-page-tools__label">Tools</span><div className="continuous-page-tools__actions">{tools.map(pageButton)}</div></div>}
  </>,toolHost) : null;
  // Dedicated destinations take precedence over a page's contextual lens.
  // Resolve once so only one main button can expose aria-current.
  const currentPrimary = /^\/profile(?:\/|$)/.test(location.pathname) ? 'profile'
    : /^\/home\/toto(?:\/|$)/.test(location.pathname) ? 'toto' : active;
  const primary = <nav style={{"--navigation-active":totoCollarNavigationColor(user)}} data-collar-code={totoCollarCode(user)} ref={railRef} className="continuous-vessel-primary continuous-vessel-primary--segmented continuous-vessel-primary--icon-only" aria-label="Main navigation">
    {capsule && <span className="continuous-primary-capsule" style={capsule} aria-hidden="true" />}
    <button className="continuous-primary-mascot" type="button" aria-label="Meet TOTO" title="Meet TOTO" aria-current={currentPrimary === "toto" ? "page" : undefined} onClick={onToto}><span className="continuous-primary-cap" aria-hidden="true"><img className="toto-medallion-navigation-icon" src="/brand/toto-medallion/navigation.png" alt="" width="76" height="76"/></span><span className="continuous-primary-label">Meet TOTO</span></button>
    <button className="continuous-primary-profile" type="button" aria-label="Profile" title="Profile" aria-current={currentPrimary==='profile'?'page':undefined} onClick={onProfile}><span className="continuous-primary-socket" aria-hidden="true"><span className="continuous-primary-cap"><NavigationProfileAvatar user={user} photoSource={navigationPhoto}/></span></span><span className="continuous-primary-label">Profile</span></button>
    <span className="continuous-primary-spacer" aria-hidden="true"/>
    <div className="continuous-primary-destinations">{primaryItems.filter(item=>item.id!=="profile" && item.id!=="toto").map(item=><button className="continuous-primary-destination" style={capsule && [52,56].includes(capsule.width) ? {position:"absolute",left:capsule.left+(capsule.width===56?6:4),top:capsule.top+(capsule.width===56?6:4)+primaryItems.filter(entry=>entry.id!=="profile" && entry.id!=="toto").indexOf(item)*(capsule.width===56?44:52),width:44,height:44} : undefined} key={item.id} type="button" title={item.label} aria-label={item.label} aria-current={currentPrimary===item.id?'page':undefined} onClick={()=>select(item)}><span className="continuous-primary-socket" aria-hidden="true"><span className="continuous-primary-cap"><Glyph item={item}/></span></span><span className="continuous-primary-label">{item.label}</span></button>)}</div>
  </nav>;
  if(!host || !bounds) return <div className="continuous-navigation-fallback">{primary}<nav aria-label={label}>{destinations.map(pageButton)}</nav>{contentTools}</div>;
  return <>{contentTools}{createPortal(<div className="continuous-vessel-edge" data-navigation-layout={bounds.w<700?'bottom':'side'} style={{left:bounds.x,top:bounds.y,width:bounds.w,height:bounds.h,'--rail-left':`${bounds.x}px`,'--rail-width':`${bounds.w}px`}}>{primary}</div>,host.closest('.toto-spatial-canvas') || host.parentElement)}</>;
}
